<template>
  <page :back-to="getPreviousUrl" :title="pageTitle" size="is-desktop">
    <div slot="page-header-right">
      <state-switch
        class="mr-2"
        v-if="isEdit"
        :value="issue.state"
        :uri="`/admin/api/magazine/issues/${issue.id}`"
      ></state-switch>

      <button v-if="isEdit" class="button is-outlined mr-2 is-danger" @click="confirmDestroy">Delete</button>

      <button
        class="button is-primary"
        :class="{ 'is-loading': form.processing }"
        @click="$refs.submitButton.click()"
      >{{ isEdit ? 'Update' : 'Create' }}
      </button>
    </div>

    <form @submit.prevent="save">
      <button ref="submitButton" type="submit" class="is-hidden"></button>

      <div class="columns">
        <div class="column is-4">
          <div class="box">
            <div class="level">
              <div class="level-left">
                <strong>Issue Cover</strong>
              </div>

              <div class="level-right">
                <upload-file
                  ref="uploadFile"
                  :endpoint="$store.getters.kytebaseMediaUploadUrl"
                  :additional="{ collection: 'images' }"
                  @uploaded="onCoverUploaded"
                  auto-reset
                >
                  <template slot-scope="{progress}">
                    <div
                      v-if="progress && ! progress.isComplete()"
                    >Uploading {{ progress.percent() }}%
                    </div>

                    <image-picker
                      v-else
                      @picked="file => $refs.uploadFile.upload(file)"
                      croppable
                      :croppie="{ viewMode: 1 }"
                    >
                      <a slot="trigger">Upload Cover</a>
                    </image-picker>
                  </template>
                </upload-file>
              </div>
            </div>

            <div class="has-text-centered">
              <img v-if="cover_image_url" :src="cover_image_url" style="width: 150px;"/>
            </div>
          </div>

          <div class="box">
            <b-field label="Release Date">
              <datepicker min-date="2000-01-01" v-model="form.release_date"></datepicker>
            </b-field>

            <b-field label="Content Path">
              <b-input v-model="form.content_path"></b-input>
            </b-field>
          </div>

          <div class="box">
            <b-field label="Price (₮)">
              <b-input v-model="form.price"></b-input>
            </b-field>
          </div>
        </div>

        <div class="column is-8">
          <div class="box">
            <b-field label="Magazine">
              <magazine-select
                :current="issue ? issue.magazine : null"
                @change="magazine => form.magazine_id = magazine.id"
              ></magazine-select>
            </b-field>
          </div>

          <div class="box">
            <b-field label="Title">
              <b-input v-model="form.title"></b-input>
            </b-field>

            <b-field label="Description">
              <b-input type="textarea" v-model="form.description"></b-input>
            </b-field>

            <b-field label="Keywords" message="This keywords will be used for search.">
              <b-taginput v-model="keywords" placeholder="Keywords"></b-taginput>
            </b-field>

            <b-field
              label="Preview Pages"
              message="Insert comma separated preview page numbers. e.g: 0,1,2,3,4,5"
            >
              <b-input v-model="form.preview_pages"></b-input>
            </b-field>
          </div>
        </div>
      </div>
    </form>
  </page>
</template>

<script>
import ImagePicker from "@/components/ImagePicker";
import UploadFile from "@/components/UploadFile";
import MagazineSelect from "./../selectors/MagazineSelect";
import StateSwitch from "./StateSwitch";

export default {
  components: {
    ImagePicker,
    UploadFile,
    MagazineSelect,
    StateSwitch
  },

  props: {
    issue: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      cover_image_url: this.issue ? this.issue.cover_image_url : null,

      form: new window.Form({
        content_path: this.issue ? this.issue.content.path : null,
        title: this.issue ? this.issue.title : null,
        price: this.issue ? this.issue.price : null,
        magazine_id: this.issue ? this.issue.magazine_id : null,
        release_date: this.issue ? this.issue.release_date : null,
        description: this.issue ? this.issue.description : null,
        keywords: this.issue ? this.issue.keywords : null,
        cover_image_path: this.issue ? this.issue.cover_image_path : null,
        cover_sizes: this.issue ? this.issue.cover_sizes : null,
        preview_pages: this.issue
          ? this.issue.preview_pages
          : "0,3,5,10,15,18,20",
      })
    };
  },

  computed: {
    isEdit() {
      return this.issue != null;
    },

    getPreviousUrl() {
      return localStorage.getItem('previous');
    },

    pageTitle() {
      if (this.isEdit) {
        return "Magazine / Issues - " + this.issue.id;
      }

      return "Magazine / Issues / Create";
    },

    keywords: {
      get() {
        return this.form && this.form.keywords && this.form.keywords.length > 0
          ? this.form.keywords.split(",")
          : [];
      },

      set(value) {
        this.form.keywords = value.join(",");
      }
    },
  },

  methods: {
    save() {
      this.isEdit ? this.update() : this.create();
    },

    create() {
      this.form.post(`/admin/api/magazine/issues`).then(this.whenSaved);
    },

    update() {
      this.form
        .put(`/admin/api/magazine/issues/${this.issue.id}`)
        .then(this.whenSaved);
    },

    whenSaved() {
      this.$buefy.toast.open("Issue saved.");

      this.$router.push("/magazine/magazine/issues");
    },

    onCoverUploaded({data: image}) {
      this.form.cover_image_path = image.path;
      this.cover_image_url = image.url;
      this.form.cover_sizes = image.cover_sizes
    },

    confirmDestroy() {
      this.$buefy.dialog.confirm({
        title: "Deleting issue",
        message:
          "Are you sure you want to <b>delete</b> this issue? This action cannot be undone.",
        confirmText: "Delete Issue",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.destroy()
      });
    },

    destroy() {
      let loading = this.$buefy.loading.open();

      new window.Form()
        .delete(`/admin/api/magazine/issues/${this.issue.id}`)
        .then(() => {
          this.$buefy.toast.open("Issue deleted.");

          loading.close();

          this.$router.push("/magazine/magazine/issues");
        })
        .catch(() => loading.close());
    }
  }
};
</script>
